import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaMapMarkerAlt, FaPaperPlane, FaGlobeAmericas, 
  FaChevronRight, FaCheckCircle, FaStar, FaChevronDown, 
  FaTimesCircle, FaClock, FaBus, FaHotel, FaUtensils, FaShieldAlt,
  FaShip, FaWater
} from 'react-icons/fa';
import './AndamanIslandParadise.css';

const AndamanIslandParadis = () => {
  const [openSection, setOpenSection] = useState(null);

  const itineraryRef = useRef(null);
  const highlightsRef = useRef(null);
  const inclusionsRef = useRef(null);
  const aboutRef = useRef(null);

  const scrollToSection = (ref) => {
    window.scrollTo({ top: ref.current.offsetTop - 100, behavior: 'smooth' });
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } }
  };

  const itineraryData = [
    {
      day: 1,
      title: "Arrival at Port Blair & Local Sightseeing",
      points: [
        "Arrival at Port Blair Airport where our representative will assist you with hotel transfer.",
        "Places to visit: Cellular Jail, Samudrika Marine Museum, Anthropological Museum, and Corbyn’s Cove Beach.",
        "Evening: Enjoy the famous Light and Sound Show at Cellular Jail.",
        "Overnight stay in Port Blair."
      ],
      icon: <FaBus />
    },
    {
      day: 2,
      title: "Ross Island & North Bay Island Excursion",
      points: [
        "After breakfast, proceed for an island excursion.",
        "Visit Ross Island: Former administrative headquarters with historic ruins.",
        "Visit North Bay Island: Famous for coral reefs and marine life.",
        "Optional activities: Scuba diving, snorkeling, and glass-bottom boat rides (extra cost).",
        "Overnight stay in Port Blair."
      ],
      icon: <FaShip />
    },
    {
      day: 3,
      title: "Baratang Island Excursion (Limestone Caves)",
      points: [
        "Early morning journey through dense forests and mangrove creeks.",
        "Experience a unique Mangrove Forest Boat Ride.",
        "Visit the natural Limestone Caves.",
        "Return to Port Blair for overnight stay."
      ],
      icon: <FaGlobeAmericas />
    },
    {
      day: 4,
      title: "Port Blair to Havelock Island (Radhanagar Beach)",
      points: [
        "Transfer to the harbor to board a cruise to Havelock Island.",
        "Visit Radhanagar Beach – ranked among Asia’s most beautiful beaches.",
        "Visit Kalapathar Beach – known for white sand and black rock formations.",
        "Overnight stay in Havelock Island resort."
      ],
      icon: <FaShip />
    },
    {
      day: 5,
      title: "Elephant Beach Excursion",
      points: [
        "Proceed for an excursion to Elephant Beach, a top snorkeling destination.",
        "Enjoy activities like snorkeling, scuba diving, and water sports (at additional cost).",
        "Relax amidst the tropical beauty and crystal-clear waters.",
        "Overnight stay in Havelock Island."
      ],
      icon: <FaWater />
    },
    {
      day: 6,
      title: "Havelock to Neil Island",
      points: [
        "Transfer to Neil Island by cruise after breakfast.",
        "Visit Bharatpur Beach: Ideal for swimming and water activities.",
        "Visit Lakshmanpur Beach: Famous for breathtaking sunset views.",
        "Overnight stay in Neil Island."
      ],
      icon: <FaShip />
    },
    {
      day: 7,
      title: "Neil Island to Port Blair via Natural Bridge",
      points: [
        "Visit the Natural Rock Formation (Howrah Bridge).",
        "Transfer to the jetty to board the cruise back to Port Blair.",
        "Upon arrival, transfer to the hotel.",
        "Overnight stay in Port Blair."
      ],
      icon: <FaMapMarkerAlt />
    },
    {
      day: 8,
      title: "Departure from Port Blair",
      points: [
        "Enjoy your final island breakfast and check out from the hotel.",
        "Transfer to Port Blair Airport for your onward journey.",
        "Tour concludes with beautiful memories of the Andaman Islands."
      ],
      icon: <FaCheckCircle />
    }
  ];

  return (
    <div className="trip-container">
      {/* 1. HEADER */}
      <motion.header initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="trip-header">
        <div className="header-left">
          <h1 className="trip-title">Andaman Island Paradise Tour – <br /> Port Blair, Havelock & Neil Island</h1>
          <p className="trip-duration">Duration: 7 Nights & 8 Days of Island Bliss</p>
        </div>
        <div className="header-right">
          <div className="info-row"><FaMapMarkerAlt className="header-icon" /> <div className="info-text"><span>Destinations:</span><p>Port Blair, Havelock, Neil, Baratang</p></div></div>
          <div className="info-row"><FaPaperPlane className="header-icon" /> <div className="info-text"><span>Service Type:</span><p>Private & Personalized</p></div></div>
          <div className="info-row type-tag"><FaGlobeAmericas className="header-icon" /><p>Tropical Island Getaway</p></div>
        </div>
      </motion.header>

      {/* 2. IMAGE GRID */}
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="image-grid-container">
        <div className="grid-item large-vertical">
          <img src="https://images.unsplash.com/photo-1599325601183-042bed55081c?q=80&w=928&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Radhanagar Beach" />
        </div>
        <div className="grid-item middle-box">
          <img src="https://plus.unsplash.com/premium_photo-1661940254003-c3f37e5d32ad?q=80&w=870&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Andaman Sea" />
        </div>
        <div className="right-stack">
          <div className="grid-item small-h">
            <img src="https://images.unsplash.com/photo-1630569688747-0e3b60f6430f?q=80&w=914&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Cellular Jail" />
          </div>
          <div className="grid-item small-h">
            <img src="https://images.unsplash.com/photo-1723203606060-b0390c387a81?q=80&w=870&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Havelock Island" />
          </div>
        </div>
      </motion.div>

      {/* 3. STICKY NAV */}
      <nav className="sticky-nav">
        <button className="tab-link" onClick={() => scrollToSection(itineraryRef)}>Detailed Itinerary</button>
        <button className="tab-link" onClick={() => scrollToSection(highlightsRef)}>Highlights</button>
        <button className="tab-link" onClick={() => scrollToSection(inclusionsRef)}>Policy & Inclusions</button>
        <button className="tab-link" onClick={() => scrollToSection(aboutRef)}>About Trip</button>
      </nav>

      <div className="content-section-wrapper">
        {/* ITINERARY SECTION */}
        <section ref={itineraryRef} className="scroll-section">
          <h2 className="section-main-title">Tour Itinerary</h2>
          <div className="days-list">
            {itineraryData.map((item) => (
              <motion.div key={item.day} initial="hidden" whileInView="visible" viewport={{ once: false, amount: 0.2 }} variants={fadeInUp} className="day-block">
                <div className="day-header">
                  <span className="day-badge">Day {item.day}</span>
                  <h3>{item.title}</h3>
                </div>
                <div className="day-body">
                  {item.points.map((p, i) => (
                    <div key={i} className="point-item">
                      <FaChevronRight className="bullet-icon" />
                      <p>{p}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* HIGHLIGHTS SECTION */}
        <motion.section ref={highlightsRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <h2 className="section-main-title">Trip Highlights</h2>
          <div className="highlights-grid">
            <div className="h-item"><FaStar className="star-icon" /> <p>Visit the historic Cellular Jail and Light & Sound Show</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Explore Ross Island and North Bay Island coral reefs</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Discover Mangrove creeks and Limestone Caves at Baratang</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Relax at Asia’s famous Radhanagar Beach</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Snorkeling and water activities at Elephant Beach</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Sunset views at Bharatpur and Lakshmanpur Beaches (Neil Island)</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Witness the Natural Rock Formation (Howrah Bridge)</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Scenic cruise transfers between tropical islands</p></div>
          </div>
        </motion.section>

        {/* INCLUSIONS & EXCLUSIONS */}
        <motion.section ref={inclusionsRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <div className="policy-box inclusion-box">
            <h4 className="p-title green"><FaCheckCircle /> What's Included</h4>
            <ul className="p-list">
              <li><FaHotel /> Accommodation in 3-star A/C hotels and beach resorts (7 Nights)</li>
              <li><FaClock /> 3N Port Blair | 2N Havelock | 1N Neil | 1N Port Blair</li>
              <li><FaUtensils /> Meal Plan: Daily Breakfast and Dinner</li>
              <li><FaBus /> Private A/C vehicle for all sightseeing and transfers</li>
              <li><FaShip /> Cruise transfers between Port Blair, Havelock, and Neil</li>
              <li><FaCheckCircle /> All entry fees, permits, and Baratang boat ride tickets</li>
              <li><FaWater /> Complimentary snorkeling experience</li>
              <li><FaStar /> 10% discount voucher for Scuba Diving</li>
              <li><FaShieldAlt /> 24/7 coordination and local expert guidance</li>
            </ul>
          </div>
          <div className="policy-box exclusion-box">
            <h4 className="p-title red"><FaTimesCircle /> What's Excluded</h4>
            <ul className="p-list">
              <li><FaTimesCircle /> Airfare or train fare (unless specified)</li>
              <li><FaUtensils /> Lunch or beverages not mentioned in the plan</li>
              <li><FaTimesCircle /> Adventure water sports (Scuba, Sea Walk etc. - extra cost)</li>
              <li><FaTimesCircle /> Camera or video camera fees at attractions</li>
              <li><FaTimesCircle /> Personal expenses (Laundry, tips, shopping)</li>
              <li><FaShieldAlt /> Travel insurance or medical expenses</li>
              <li><FaTimesCircle /> Costs due to unforeseen weather or flight delays</li>
            </ul>
          </div>
        </motion.section>

        {/* ABOUT SECTION */}
        <motion.section ref={aboutRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <h2 className="section-main-title">About This Journey</h2>
          <p className="about-text">
            The Andaman Island Paradise Tour is an unforgettable journey through the tropical beauty of the Andaman Islands. This tour takes travelers across the most stunning destinations including Port Blair, Havelock Island, Neil Island, and Baratang Island, offering a perfect mix of beaches, marine life, nature, and island culture.
            <br /><br />
            From the historic Cellular Jail in Port Blair to the coral reefs of North Bay and the limestone caves of Baratang, travelers will experience the rich natural beauty and history of the islands. Guests can enjoy crystal-clear waters, snorkeling experiences, island cruises, and peaceful beach sunsets, making this tour ideal for nature lovers and adventure seekers.
          </p>
        </motion.section>

        {/* TERMS & PRIVACY */}
        <div className="accordion-item">
          <Link to="/terms-conditions" className="accordion-header" style={{ textDecoration: 'none', color: 'inherit' }}>
            <span>Terms & Conditions</span>
            <FaChevronDown className="arrow-icon" style={{ transform: 'rotate(-90deg)' }} /> 
          </Link>
        </div>

        <div className="accordion-item">
          <Link to="/privacy-policy" className="accordion-header" style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
            <span>Privacy Policy</span>
            <FaChevronDown className="arrow-icon" style={{ transform: 'rotate(-90deg)' }} />
          </Link>
        </div>

        {/* TRAVEL INFO */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section travel-info-grid">
          <div className="info-card note-card" style={{ width: '100%' }}>
            <h4 className="info-card-title"><FaPaperPlane /> Important Note</h4>
            <p>Flight and train tickets can be arranged by <strong>Chandrama Holidays</strong> on request at the best available fares. Our team will assist you in planning convenient travel arrangements for a smooth journey.</p>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default AndamanIslandParadis;